"use client";

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import type { EmailAgentConfig } from "@/lib/agents/email/types";
import type { ComposedRoleBundle } from "@/lib/ai/roles/compose";
import { useTranslations } from "next-intl"


// Re-export for backward compatibility
export type { EmailAgentConfig } from "@/lib/agents/email/types";

/**
 * OAuth status for inbox connection
 */
export type OAuthStatus = 'connected' | 'token_expired' | 'error';

export interface EmailAgentInbox {
  id: string;
  address: string;
  provider: string;
  oauth_status?: OAuthStatus;
}

export interface EmailAgent {
  id: string;
  tenant_id: string;
  created_by?: string;
  name: string;
  inboxes: EmailAgentInbox[];
  // Note: system_prompt moved to scoped tenant_ai_configs (use /api/agents/email/auto-reply/system-prompt)
  status: "draft" | "active" | "disabled";
  config?: EmailAgentConfig;
  created_at: string;
  updated_at: string;
}

export interface CreateEmailAgentData {
  name: string;
  inbox_ids?: string[];
  /** Agent role slugs (ai_use_case_roles); defaults to ['custom'] server-side. */
  roles?: string[];
  /** Prompts from the preview, so apply reuses that ONE composition instead of generating again. */
  composed?: ComposedRoleBundle;
  /** Display name; only meaningful for the custom-only role set. */
  role_label?: string | null;
}

export interface UpdateEmailAgentData {
  name?: string;
  status?: "draft" | "active" | "disabled";
  /** Role change; pairs with apply_preset. */
  roles?: string[];
  /** Prompts from the preview; ignored unless apply_preset is on. */
  composed?: ComposedRoleBundle;
  /** false = metadata-only change (default true — apply, overwriting prompt/classifier). */
  apply_preset?: boolean;
  /** Display name; cleared server-side on a named preset. */
  role_label?: string | null;
}

export function useEmailAgents() {
  const tUi = useTranslations("Feedback.UseAgents")

  const [agents, setAgents] = useState<EmailAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth, isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const loadAgents = useCallback(async () => {
    // If auth is still being determined, wait
    if (isAuthLoading) {
      return;
    }

    // If auth is done but user is not authenticated, stop loading
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    setError(null);

    try {
      const response = await fetchWithAuth("/api/agents/email");

      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to load agents");
      }
    } catch (err) {
      console.error("Error loading email agents:", err);
      setError(tUi("failedToLoadAgents"));
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated, isAuthLoading]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const getAgent = useCallback(
    (id: string) => {
      return agents.find((agent) => agent.id === id);
    },
    [agents],
  );

  const createAgent = useCallback(
    async (agentData: CreateEmailAgentData): Promise<EmailAgent> => {
      const response = await fetchWithAuth("/api/agents/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(agentData),
      });

      if (response.ok) {
        const data = await response.json();
        const newAgent = data.agent;
        setAgents((prev) => [newAgent, ...prev]);
        return newAgent;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create agent");
      }
    },
    [fetchWithAuth],
  );

  const updateAgent = useCallback(
    async (
      id: string,
      agentData: UpdateEmailAgentData,
    ): Promise<EmailAgent> => {
      const response = await fetchWithAuth(`/api/agents/email/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(agentData),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedAgent = data.agent;
        setAgents((prev) =>
          prev.map((agent) => (agent.id === id ? updatedAgent : agent)),
        );
        return updatedAgent;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update agent");
      }
    },
    [fetchWithAuth],
  );

  const deleteAgent = useCallback(
    async (id: string): Promise<void> => {
      const response = await fetchWithAuth(`/api/agents/email/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setAgents((prev) => prev.filter((agent) => agent.id !== id));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete agent");
      }
    },
    [fetchWithAuth],
  );

  const refreshAgents = useCallback(loadAgents, [loadAgents]);

  return {
    agents,
    isLoading,
    error,
    getAgent,
    createAgent,
    updateAgent,
    deleteAgent,
    refreshAgents,
  };
}

/**
 * Inbox with information about which agents use it
 */
export interface InboxWithUsage extends EmailAgentInbox {
  use_for_sending?: boolean;
  is_default?: boolean;
  used_by_agents: { id: string; name: string }[];
  last_error?: string | null;
  last_error_at?: string | null;
}

/**
 * Hook to fetch all tenant inboxes with agent usage info
 */
export function useAllInboxes() {
  const tUi = useTranslations("Feedback.UseAgents")

  const [inboxes, setInboxes] = useState<InboxWithUsage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth, isAuthenticated } = useAuth();

  const loadInboxes = useCallback(async () => {
    if (!isAuthenticated) {
      // Don't set isLoading to false - keep loading until auth is ready
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth("/api/agents/email/inboxes");
      const data = await response.json();

      if (response.ok) {
        setInboxes(data.inboxes || []);
      } else {
        console.error("[useAllInboxes] API error:", response.status, data);
        setError(data.error || "Failed to load inboxes");
      }
    } catch (err) {
      console.error("[useAllInboxes] Error loading inboxes:", err);
      setError(tUi("failedToLoadInboxes"));
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated]);

  useEffect(() => {
    loadInboxes();
  }, [loadInboxes]);

  const refreshInboxes = useCallback(loadInboxes, [loadInboxes]);

  return {
    inboxes,
    isLoading,
    error,
    refreshInboxes,
  };
}

/**
 * Hook to fetch available (unassigned) inboxes for creating new agents
 * @deprecated Use useAllInboxes instead which provides usage info
 */
export function useAvailableInboxes() {
  const tUi = useTranslations("Feedback.UseAgents")

  const [inboxes, setInboxes] = useState<EmailAgentInbox[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth, isAuthenticated } = useAuth();

  const loadInboxes = useCallback(async () => {
    if (!isAuthenticated) {
      // Don't set isLoading to false - keep loading until auth is ready
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth("/api/agents/email/inboxes/available");

      if (response.ok) {
        const data = await response.json();
        setInboxes(data.inboxes || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to load inboxes");
      }
    } catch (err) {
      console.error("Error loading available inboxes:", err);
      setError(tUi("failedToLoadInboxes"));
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated]);

  useEffect(() => {
    loadInboxes();
  }, [loadInboxes]);

  const refreshInboxes = useCallback(loadInboxes, [loadInboxes]);

  return {
    inboxes,
    isLoading,
    error,
    refreshInboxes,
  };
}
